import { createContext, useContext } from 'react';
import { LocaleContext, type LocaleValue } from '../contexts/LocaleContext';
import type { TenantZoneState } from '../lib/tenant-zone';

/** Stands in when a suite replaces the LocaleContext module with a mock that
 *  exports only `useLocale` — the context object is then undefined, and
 *  useContext(undefined) is an error rather than an absent value. */
const NO_LOCALE = createContext<LocaleValue | undefined>(undefined);

const NO_PROVIDER: TenantZoneState = { status: 'loading' };

/**
 * The tenant zone's three states — loading, ready, error — from the
 * LocaleProvider. See lib/tenant-zone.ts for what each one means and why a
 * guessed zone is never one of them.
 *
 * Outside a provider this is `loading`, never a default: a component with no
 * provider above it has no way to know the tenant.
 */
export function useTenantZone(): TenantZoneState {
  const ctx = useContext(LocaleContext ?? NO_LOCALE);
  if (ctx?.zone) return ctx.zone;
  // A suite that hands the context a bare `{ locale: { time_zone } }` value.
  const legacy = ctx?.locale?.time_zone;
  return typeof legacy === 'string' && legacy.length > 0
    ? { status: 'ready', zone: legacy, source: 'server' }
    : NO_PROVIDER;
}

/**
 * The tenant's IANA zone — `companies.time_zone` — or null when it is not
 * known yet (or could not be loaded).
 *
 * Null rather than a throw or a guessed zone: with no zone there is no honest
 * day, and every zone-taking helper in lib/calendar-date renders that as
 * absent. Inside the app every authenticated route sits under
 * `TenantZoneGate`, so a screen only renders once this is a string.
 */
export function useTenantTimeZone(): string | null {
  const zone = useTenantZone();
  return zone.status === 'ready' ? zone.zone : null;
}
